import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Button, Input, Skeleton } from '@shigo-ui/ui';

import { TargetingLocationType, type TargetingLocation } from '../../../model';
import type { GeolocationTargetingClient } from '../../../api';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../lib/messages';
import { toggleStringValue } from '../TargetingForm.helpers';
import { GeoNodeRow } from '../GeoNodeRow';
import { GeolocationSelectionPanel } from '../GeolocationSelectionPanel';
import {
  applyGeoNodePatches,
  geoLocationKey,
  geoLocationMetaLabel,
  geoSearchResultType,
  loadGeoChildren,
  toGeoNode,
  toTargetingLocation,
  type GeoNode,
} from '../GeolocationTargeting.helpers';

const targetingLookupQueryOptions = {
  staleTime: 10 * 60 * 1000,
  retry: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
} as const;

export function GeolocationTargeting({
  client,
  onSelectedLocationsChange,
  selectedLocations,
}: {
  client: GeolocationTargetingClient;
  onSelectedLocationsChange: (locations: TargetingLocation[]) => void;
  selectedLocations: TargetingLocation[];
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const search = useDebouncedValue(searchTerm, setSearchTerm, 800);
  const draftTerm = search.draft.trim();
  const settledTerm = searchTerm.trim();
  const searchPending = draftTerm !== settledTerm;
  const searchQuery = useQuery({
    queryKey: [...(client.queryScope ?? []), 'targeting', 'geolocations', 'search', settledTerm],
    queryFn: () => client.searchLocations({ searchTerm: settledTerm }),
    enabled: draftTerm.length >= 3 && !searchPending,
    ...targetingLookupQueryOptions,
  });
  const [nodePatches, setNodePatches] = useState<Record<string, Partial<GeoNode>>>({});
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const continentsQuery = useQuery({
    queryKey: [...(client.queryScope ?? []), 'targeting', 'geolocations', 'continents'],
    queryFn: () => client.listContinents(),
    ...targetingLookupQueryOptions,
  });
  const nodes = (continentsQuery.data ?? [])
    .map((option) => toGeoNode(option, TargetingLocationType.Continent))
    .map((node) => applyGeoNodePatches(node, nodePatches));

  async function toggleExpanded(node: GeoNode) {
    const nextExpanded = toggleStringValue(expandedIds, node.id, !expandedIds.includes(node.id));
    setExpandedIds(nextExpanded);
    if (expandedIds.includes(node.id) || node.children) {
      return;
    }

    setNodePatches((current) => ({
      ...current,
      [node.id]: { ...current[node.id], loading: true },
    }));
    try {
      const children = await loadGeoChildren(client, node);
      setNodePatches((current) => ({
        ...current,
        [node.id]: { ...current[node.id], children, loading: false },
      }));
    } catch {
      setNodePatches((current) => ({
        ...current,
        [node.id]: { ...current[node.id], loading: false },
      }));
    }
  }

  function selectNode(node: GeoNode, excluded: boolean) {
    const nextLocation = toTargetingLocation(node, excluded);
    const filtered = selectedLocations.filter((location) => geoLocationKey(location) !== node.id);
    onSelectedLocationsChange([...filtered, nextLocation]);
  }

  function removeLocation(location: TargetingLocation) {
    onSelectedLocationsChange(
      selectedLocations.filter((item) => geoLocationKey(item) !== geoLocationKey(location)),
    );
  }

  let continentsContent = (
    <div className="grid gap-1">
      {nodes.map((node) => (
        <GeoNodeRow
          key={node.id}
          expandedIds={expandedIds}
          node={node}
          onSelect={selectNode}
          onToggleExpanded={(selectedNode) => {
            void toggleExpanded(selectedNode);
          }}
          selectedLocations={selectedLocations}
        />
      ))}
    </div>
  );
  if (continentsQuery.isLoading) {
    continentsContent = <Skeleton className="h-20" />;
  } else if (continentsQuery.isError) {
    continentsContent = (
      <div role="alert" className="grid justify-items-start gap-3 p-3">
        <p className="text-destructive text-sm">{dashboardT('targeting.loadGeolocationsError')}</p>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            void continentsQuery.refetch();
          }}
        >
          {dashboardT('targeting.retry')}
        </Button>
      </div>
    );
  }

  let locationsContent = continentsContent;
  if (draftTerm) {
    locationsContent = (
      <div className="grid gap-1">
        {(searchQuery.data ?? []).map((option) => {
          const node = toGeoNode(option, geoSearchResultType(option));
          return (
            <GeoNodeRow
              key={node.id}
              node={node}
              expandedIds={[]}
              expandable={false}
              description={geoLocationMetaLabel(toTargetingLocation(node, false))}
              onSelect={selectNode}
              onToggleExpanded={() => {}}
              selectedLocations={selectedLocations}
            />
          );
        })}
      </div>
    );
    if (draftTerm.length < 3) {
      locationsContent = (
        <p role="status" className="text-muted-foreground p-3 text-sm">
          {dashboardT('targeting.geoSearchHint')}
        </p>
      );
    } else if (searchPending || searchQuery.isLoading) {
      locationsContent = (
        <div role="status" aria-label={dashboardT('targeting.geoSearchLoading')}>
          <Skeleton className="h-20" />
        </div>
      );
    } else if (searchQuery.isError) {
      locationsContent = (
        <div role="alert" className="grid justify-items-start gap-3 p-3">
          <p className="text-destructive text-sm">
            {dashboardT('targeting.loadGeolocationsError')}
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void searchQuery.refetch();
            }}
          >
            {dashboardT('targeting.retry')}
          </Button>
        </div>
      );
    } else if (searchQuery.data?.length === 0) {
      locationsContent = (
        <p role="status" className="text-muted-foreground p-3 text-sm">
          {dashboardT('targeting.geoSearchEmpty')}
        </p>
      );
    }
  }

  return (
    <div className="border-border grid overflow-hidden rounded-md border lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="border-border bg-background/40 min-w-0 border-b lg:border-r lg:border-b-0">
        <div className="border-border border-b p-2">
          <Input
            type="search"
            aria-label={dashboardT('targeting.geoSearch')}
            placeholder={dashboardT('targeting.geoSearch')}
            value={search.draft}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.preventDefault();
            }}
            onChange={(event) => {
              search.setDraft(event.target.value);
            }}
          />
        </div>
        <div className="max-h-[520px] overflow-auto p-2">{locationsContent}</div>
      </div>
      <GeolocationSelectionPanel
        locations={selectedLocations}
        onClear={() => {
          onSelectedLocationsChange([]);
        }}
        onRemove={removeLocation}
      />
    </div>
  );
}
